import React, { useEffect } from "react";
import "./ConfirmModal.css";

/**
 * Reusable confirmation modal.
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {string} title - Modal title
 * @param {string} message - Body text (can include JSX)
 * @param {string} confirmLabel - Text for confirm button (e.g. "Delete")
 * @param {string} cancelLabel - Text for cancel button
 * @param {() => void} onConfirm - Called when confirm is clicked
 * @param {() => void} onCancel - Called when cancel or overlay is clicked
 * @param {string} variant - "danger" (red confirm) | "primary" (default blue)
 */
function ConfirmModal({
  isOpen,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  variant = "primary",
}) {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  return (
    <div
      className="confirm-modal-overlay"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
    >
      <div
        className="confirm-modal-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="confirm-modal-icon" data-variant={variant}>
          {variant === "danger" ? (
            <span className="confirm-modal-icon-symbol" aria-hidden>⚠</span>
          ) : (
            <span className="confirm-modal-icon-symbol" aria-hidden>?</span>
          )}
        </div>
        <h2 id="confirm-modal-title" className="confirm-modal-title">
          {title}
        </h2>
        <div className="confirm-modal-message">{message}</div>
        <div className="confirm-modal-actions">
          <button
            type="button"
            className="confirm-modal-btn confirm-modal-cancel"
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`confirm-modal-btn confirm-modal-confirm confirm-modal-${variant}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmModal;
